import { createSlice } from "@reduxjs/toolkit"
import {
  primaryTabs,
  resourceBudgetType,
  secondayTabs,
} from "../../pages/ResourceBudget/types"
import {
  primaryTabs as pTabs,
  secondayTabs as sTabs,
} from "../../pages/ResourceBudget/constants"
import { toast } from "react-toastify"
import {
  clinicalTableFooter,
  nonClinicalTableFooter,
} from "../../components/Table/ResourceBudgetTable/TableConfigs"
import {
  processBudgetData,
  calculateAggregatedTotals,
  type BudgetItem,
  type AggregatedTotals,
} from "../../components/Table/ResourceBudgetTable/calculationUtils"
import {
  checkForSanity,
  condenseNumber,
} from "../../components/Table/ResourceBudgetTable/validators"
import { updateProvider } from "../../components/Table/ResourceBudgetTable/utils"

type TabKeyType = "clinicalDoctors" | "clinicalHygienists" | "nonClinical"

// Helper to clear unsaved "+Add" row when switching tabs/months
const clearNewEntryRow = (state: resourceBudgetType) => {
  if (!state.editState.newEntry) return

  const monthState = getMonthEditState(state, state.month)

  if (state.primaryIndex === 0) {
    const tabKey = state.secondaryIndex === 0 ? "doctorsData" : "hygienistsData"
    if ((state as any)[tabKey]) {
      const arr = (state as any)[tabKey] as any[]
      ;(state as any)[tabKey] = arr.slice(1)
    }
    // also clear month-specific data/modifiedData for this tab
    if (monthState.modifiedData) {
      const modKey =
        state.secondaryIndex === 0 ? "doctorsData" : "hygienistsData"
      const modData = monthState.modifiedData[modKey]
      if (modData) {
        monthState.modifiedData[modKey] = modData.slice(1)
      }
    }
    const tabKeyMonth =
      state.secondaryIndex === 0 ? "clinicalDoctors" : "clinicalHygienists"
    if (monthState[tabKeyMonth]?.data) {
      monthState[tabKeyMonth].data = monthState[tabKeyMonth].data.slice(1)
    }
  } else {
    if (state.nonClinicalData) {
      state.nonClinicalData = state.nonClinicalData.slice(1)
    }
    if (monthState.modifiedData?.nonClinicalData) {
      monthState.modifiedData.nonClinicalData =
        monthState.modifiedData.nonClinicalData.slice(1)
    }
    if (monthState.nonClinical?.data) {
      monthState.nonClinical.data = monthState.nonClinical.data.slice(1)
    }
  }

  state.data = state.data.slice(1)
  state.editState.newEntry = false
  state.editState.clinicalDoctors.newEntry = false
  state.editState.clinicalHygienists.newEntry = false
  state.editState.nonClinical.newEntry = false
  state.editState.dirty = []
  state.editState.clinicalDoctors.dirty = []
  state.editState.clinicalHygienists.dirty = []
  state.editState.nonClinical.dirty = []
  // clear month-specific flags/dirty
  monthState.newEntry = false
  monthState.dirty = []
  monthState.clinicalDoctors.newEntry = false
  monthState.clinicalHygienists.newEntry = false
  monthState.nonClinical.newEntry = false
  monthState.clinicalDoctors.dirty = []
  monthState.clinicalHygienists.dirty = []
  monthState.nonClinical.dirty = []
}

// Helper function to get or create month-specific edit state
const getMonthEditState = (
  state: resourceBudgetType,
  month: number,
): (typeof state.editState.monthStates)[number] => {
  if (!state.editState.monthStates) {
    state.editState.monthStates = {}
  }
  if (!state.editState.monthStates[month]) {
    state.editState.monthStates[month] = {
      newEntry: false,
      dirty: [],
      clinicalDoctors: {
        dirty: [],
        newEntry: false,
        data: [],
      },
      clinicalHygienists: {
        dirty: [],
        newEntry: false,
        data: [],
      },
      nonClinical: {
        dirty: [],
        newEntry: false,
        data: [],
      },
      allChanges: [],
      modifiedData: {},
    }
  }
  return state.editState.monthStates[month]
}

// Sync current month's state to main editState (current tab only)
const syncMonthStateToMain = (state: resourceBudgetType, month: number) => {
  const monthState = getMonthEditState(state, month)
  const currentTabKey: TabKeyType =
    state.primaryIndex === 0
      ? state.secondaryIndex === 0
        ? "clinicalDoctors"
        : "clinicalHygienists"
      : "nonClinical"
  const currentTabState = monthState[currentTabKey]
  state.editState.newEntry = currentTabState.newEntry
  state.editState.dirty = [...currentTabState.dirty]
  state.editState.clinicalDoctors = { ...monthState.clinicalDoctors }
  state.editState.clinicalHygienists = { ...monthState.clinicalHygienists }
  state.editState.nonClinical = { ...monthState.nonClinical }
}

// Save current data into month-specific storage
const saveCurrentDataToMonth = (state: resourceBudgetType, month: number) => {
  const monthState = getMonthEditState(state, month)
  if (state.primaryIndex === 0) {
    if (state.secondaryIndex === 0 && state.doctorsData) {
      monthState.modifiedData.doctorsData = [...state.doctorsData]
    } else if (state.secondaryIndex === 1 && state.hygienistsData) {
      monthState.modifiedData.hygienistsData = [...state.hygienistsData]
    }
  } else {
    if (state.nonClinicalData) {
      monthState.modifiedData.nonClinicalData = [...state.nonClinicalData]
    }
  }
}

// Restore month-specific data (and aggregations) when switching months
const restoreMonthData = (state: resourceBudgetType, month: number) => {
  console.log("🟣 [restoreMonthData] Restoring data for month:", month, {
    primaryIndex: state.primaryIndex,
    secondaryIndex: state.secondaryIndex,
  })
  const monthState = getMonthEditState(state, month)
  if (state.primaryIndex === 0) {
    if (state.secondaryIndex === 0 && monthState.modifiedData.doctorsData) {
      console.log("🟣 [restoreMonthData] Restoring doctorsData:", {
        count: monthState.modifiedData.doctorsData.length,
        items: monthState.modifiedData.doctorsData.map((item: any) => ({
          id: item.id,
          provider_sk: item.provider_sk,
          resource_name: item.resource_name,
        })),
      })
      state.doctorsData = [...monthState.modifiedData.doctorsData]
      state.data = [...monthState.modifiedData.doctorsData]
      const aggregatedTotals = calculateAggregatedTotals(
        monthState.modifiedData.doctorsData,
      )
      state.aggregations[secondayTabs.doctors].data = {
        resource_name: `Total`,
        ...aggregatedTotals,
      }
    } else if (
      state.secondaryIndex === 1 &&
      monthState.modifiedData.hygienistsData
    ) {
      console.log("🟣 [restoreMonthData] Restoring hygienistsData:", {
        count: monthState.modifiedData.hygienistsData.length,
        items: monthState.modifiedData.hygienistsData.map((item: any) => ({
          id: item.id,
          provider_sk: item.provider_sk,
          resource_name: item.resource_name,
        })),
      })
      state.hygienistsData = [...monthState.modifiedData.hygienistsData]
      state.data = [...monthState.modifiedData.hygienistsData]
      const aggregatedTotals = calculateAggregatedTotals(
        monthState.modifiedData.hygienistsData,
      )
      state.aggregations[secondayTabs.hygienists].data = {
        resource_name: `Total`,
        ...aggregatedTotals,
      }
    }
  } else if (monthState.modifiedData.nonClinicalData) {
    console.log("🟣 [restoreMonthData] Restoring nonClinicalData:", {
      count: monthState.modifiedData.nonClinicalData.length,
      items: monthState.modifiedData.nonClinicalData.map((item: any) => ({
        id: item.id,
        resource_name: item.resource_name,
      })),
    })
    state.nonClinicalData = [...monthState.modifiedData.nonClinicalData]
    state.data = [...monthState.modifiedData.nonClinicalData]
    const aggregatedTotals = calculateAggregatedTotals(
      monthState.modifiedData.nonClinicalData,
    )
    state.aggregations[primaryTabs.nonClinical].data = {
      resource_name: `Total`,
      ...aggregatedTotals,
    }
  }
  console.log("🟣 [restoreMonthData] Final state.data:", {
    count: state.data.length,
    items: state.data.map((item: any) => ({
      id: item.id,
      provider_sk: item.provider_sk,
      resource_name: item.resource_name,
    })),
  })
}

// Sync main editState to month-specific (current tab)
const syncMainToMonthState = (state: resourceBudgetType, month: number) => {
  const monthState = getMonthEditState(state, month)
  saveCurrentDataToMonth(state, month)
  const currentTabKey: TabKeyType =
    state.primaryIndex === 0
      ? state.secondaryIndex === 0
        ? "clinicalDoctors"
        : "clinicalHygienists"
      : "nonClinical"
  const currentTabState = state.editState[currentTabKey]
  monthState.newEntry = currentTabState.newEntry
  monthState[currentTabKey].newEntry = currentTabState.newEntry
  monthState[currentTabKey].dirty = [...currentTabState.dirty]
  monthState.dirty = [...currentTabState.dirty]
  monthState.clinicalDoctors.data = [...state.editState.clinicalDoctors.data]
  monthState.clinicalHygienists.data = [
    ...state.editState.clinicalHygienists.data,
  ]
  monthState.nonClinical.data = [...state.editState.nonClinical.data]
}

const upsertChangeForTab = (
  state: resourceBudgetType,
  tabKey: TabKeyType,
  change: Record<string, any>,
  month?: number,
) => {
  const currentMonth = month ?? state.month
  const monthState = getMonthEditState(state, currentMonth)
  const target = monthState[tabKey]
  if (!target) return

  const payload: Record<string, any> & {
    tabKey: TabKeyType
    timestamp: number
    month: number
  } = {
    ...change,
    tabKey,
    month: currentMonth,
    timestamp: Date.now(),
  }

  const rowId = payload.id ?? payload.resource_id ?? payload.row_index
  if (!rowId && rowId !== 0) {
    return
  }

  const existingIndex = target.data.findIndex((item: any) => {
    const itemId = item.id ?? item.resource_id ?? item.row_index
    return itemId === rowId
  })

  if (existingIndex !== -1) {
    target.data[existingIndex] = {
      ...target.data[existingIndex],
      ...payload,
    }
  } else {
    target.data.push(payload)
  }

  const globalIndex = state.editState.allChanges.findIndex((item: any) => {
    const itemId = item.id ?? item.resource_id ?? item.row_index
    const itemMonth = item.month ?? currentMonth
    return itemId === rowId && itemMonth === currentMonth
  })

  if (globalIndex !== -1) {
    state.editState.allChanges[globalIndex] = {
      ...state.editState.allChanges[globalIndex],
      ...payload,
    }
  } else {
    state.editState.allChanges.push(payload)
  }
}

const initialState: resourceBudgetType = {
  primaryTabs: Object.values(pTabs),
  secondayTabs: [Object.values(sTabs)],
  secondaryIndex: 0,
  primaryIndex: 0,
  dataIndex: secondayTabs.doctors,
  resourceBudget: true,
  years: [],
  selectedYear: null,
  month: 1,
  copyTo: {
    data: {},
    selectAll: false,
    selected: [],
  },
  data: [],
  published: true,
  providerState: {
    doctors: [],
    hygienists: [],
    loading: false,
    // Keep 'data' for backward compatibility, but prefer doctors/hygienists
    data: [],
  },
  aggregations: {
    [primaryTabs.nonClinical]: {
      data: { resource_name: "Total" },
      key: "Staff",
    },
    [secondayTabs.doctors]: {
      data: { resource_name: "Total" },
      key: "Doctors",
    },
    [secondayTabs.hygienists]: {
      data: { resource_name: "Total" },
      key: "Hygienists",
    },
  },
  editState: {
    // Month-specific states
    monthStates: {},
    newEntry: false,
    dirty: [], // Now tracks cell IDs as strings
    clinicalDoctors: {
      dirty: [],
      newEntry: false,
      data: [],
    },
    clinicalHygienists: {
      dirty: [],
      newEntry: false,
      data: [],
    },
    nonClinical: {
      dirty: [],
      newEntry: false,
      data: [],
    },
    // Track all changes for sync to P&L (aggregated across all months)
    allChanges: [],
  },
}

const budgetSlice = createSlice({
  name: "resource-budget",
  initialState,
  reducers: {
    setPrimaryTab: (state, action) => {
      clearNewEntryRow(state)
      state.primaryIndex = action.payload
      state.dataIndex =
        action.payload === 0
          ? state.secondaryIndex === 0
            ? secondayTabs.doctors
            : secondayTabs.hygienists
          : primaryTabs.nonClinical

      // Load the correct data for the selected primary tab
      if (action.payload === 0) {
        // Clinical mode - load data for the current secondary tab
        const tabDataKey =
          state.secondaryIndex === 0 ? "doctorsData" : "hygienistsData"

        // Always set data, even if empty, to ensure proper data isolation
        if (state[tabDataKey as keyof typeof state]) {
          const tabData = state[tabDataKey as keyof typeof state] as any[]
          console.log(
            "🟡 [setPrimaryTab] Setting state.data from",
            tabDataKey,
            {
              count: tabData.length,
              items: tabData.map((item: any) => ({
                id: item.id,
                provider_sk: item.provider_sk,
                resource_name: item.resource_name,
                isTotal: item.resource_name === "Total",
              })),
            },
          )
          // Filter out any "Total" rows that might have been accidentally added
          const filteredData = tabData.filter(
            (item: any) => item.resource_name !== "Total",
          )
          if (filteredData.length !== tabData.length) {
            console.warn(
              "⚠️ [setPrimaryTab] Found and removed Total row from data!",
            )
          }
          state.data = [...filteredData]
        } else {
          // Clear data if the clinical tab has no data
          state.data = []
          // Also reset aggregations for empty clinical tabs
          const currentDataIndex =
            state.secondaryIndex === 0
              ? secondayTabs.doctors
              : secondayTabs.hygienists
          state.aggregations[currentDataIndex].data = { ...clinicalTableFooter }
        }

        const monthState = getMonthEditState(state, state.month)
        const tabKey =
          state.secondaryIndex === 0 ? "clinicalDoctors" : "clinicalHygienists"
        state.editState.dirty = [...monthState[tabKey].dirty]
        state.editState.newEntry = monthState[tabKey].newEntry
      } else {
        // Non-clinical mode - load non-clinical data from stored field
        if (state.nonClinicalData) {
          state.data = [...state.nonClinicalData]
        } else {
          // Clear data if the non-clinical tab has no data
          state.data = []
          // Also reset aggregations for empty non-clinical tab
          state.aggregations[primaryTabs.nonClinical].data = {
            ...nonClinicalTableFooter,
          }
        }

        const monthState = getMonthEditState(state, state.month)
        state.editState.dirty = [...monthState.nonClinical.dirty]
        state.editState.newEntry = monthState.nonClinical.newEntry
      }

      // Keep state persistent - no more resetting
      // state.editState = initialState.editState
      // state.aggregations = initialState.aggregations
      // state.copyTo = initialState.copyTo
      // state.providerState = initialState.providerState
    },
    setSecondayTab: (state, action) => {
      clearNewEntryRow(state)
      state.secondaryIndex = action.payload
      state.dataIndex =
        action.payload === 0 ? secondayTabs.doctors : secondayTabs.hygienists

      // Load the correct data for the selected secondary tab
      if (state.primaryIndex === 0) {
        // Clinical mode
        const secondaryTabKey =
          action.payload === 0 ? secondayTabs.doctors : secondayTabs.hygienists
        const tabDataKey =
          action.payload === 0 ? "doctorsData" : "hygienistsData"

        // Always set data, even if empty, to ensure proper data isolation
        if (state[tabDataKey as keyof typeof state]) {
          state.data = [...(state[tabDataKey as keyof typeof state] as any[])]
        } else {
          // Clear data if the clinical tab has no data
          state.data = []
          // Also reset aggregations for empty clinical tabs
          const currentDataIndex =
            action.payload === 0
              ? secondayTabs.doctors
              : secondayTabs.hygienists
          state.aggregations[currentDataIndex].data = { ...clinicalTableFooter }
        }

        const monthState = getMonthEditState(state, state.month)
        const tabKey =
          action.payload === 0 ? "clinicalDoctors" : "clinicalHygienists"
        state.editState.dirty = [...monthState[tabKey].dirty]
        state.editState.newEntry = monthState[tabKey].newEntry
      }

      // Keep state persistent - no more resetting
      // state.editState = initialState.editState
      // state.copyTo = initialState.copyTo
      // state.providerState = initialState.providerState
    },
    togglePublished: (state: any, _) => {
      state.published = true
      state.editState.newEntry = false
    },
    setYears: (state, action) => {
      state.years = action.payload
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload
    },
    loadProviders: (state: any, action) => {
      // Store providers separately for doctors and hygienists based on current secondaryIndex
      if (state.primaryIndex === 0) {
        // Clinical mode - store in the appropriate tab
        if (state.secondaryIndex === 0) {
          state.providerState.doctors = action.payload
        } else if (state.secondaryIndex === 1) {
          state.providerState.hygienists = action.payload
        }
      }
      // Also update 'data' for backward compatibility
      state.providerState.data = action.payload
    },
    loadingProviders: (state, action) => {
      state.providerState.loading = action.payload
    },
    appendProvider: (state, _) => {
      if (state.editState.newEntry) {
        return state
      }
      let newProvider: any = {}

      if (state.primaryIndex === 1) {
        newProvider["resource_name"] = ""
        newProvider["role_category"] = "Select role"
      } else {
        newProvider["resource_name"] = "Select Provider"
      }

      // Create new provider with all required fields
      if (state.primaryIndex === 0) {
        // Clinical mode
        newProvider = {
          resource_name: "Select Provider", // Ensure this is set correctly
          ...clinicalTableFooter,
          id: Date.now(), // Add unique ID
        }
      } else {
        // Non-clinical mode
        newProvider = {
          resource_name: "",
          role_category: "Select role",
          ...nonClinicalTableFooter,
          id: Date.now(), // Add unique ID
        }
      }

      // Add new row to the current tab's data
      if (state.primaryIndex === 0) {
        // Clinical mode - add to the specific tab's data
        const tabKey =
          state.secondaryIndex === 0 ? "doctorsData" : "hygienistsData"
        const currentTabData = state[tabKey] || []
        const newData = [newProvider, ...currentTabData]
        state[tabKey] = newData
        // Update state.data to show the new row (preserve existing data structure)
        state.data = [...newData]
      } else {
        // Non-clinical mode - add to nonClinicalData and state.data
        const newData = [newProvider, ...state.data]
        state.nonClinicalData = [...newData]
        state.data = [...newData]
      }

      const monthState = getMonthEditState(state, state.month)
      if (state.primaryIndex === 0) {
        const tabKey =
          state.secondaryIndex === 0 ? "clinicalDoctors" : "clinicalHygienists"
        monthState[tabKey].newEntry = true
      } else {
        monthState.nonClinical.newEntry = true
      }
      monthState.newEntry = true
      syncMonthStateToMain(state, state.month)
      // Don't shift existing dirty cells - new row is at index 0
      // state.editState.dirty = state.editState.dirty.map((key) => key + 1)
    },
    updateAppendedProvider: (state, action) => {
      const { index, value, field, fieldFunc } = action.payload

      // value change check
      if (action.payload?.original[field] == value) return state

      const disableCheck =
        state.dataIndex !== pTabs.nonClinical && field === "resource_name"
      // sanity check
      const sanityValue = checkForSanity(
        { field, value, digitsLimit: 5 },
        disableCheck,
      )
      if (!sanityValue?.valid) {
        toast.error(sanityValue?.message)
        fieldFunc?.()
        return state
      }

      const dataIndex = state.dataIndex
      const tabKeyForPersistence: TabKeyType =
        state.primaryIndex === 0
          ? state.secondaryIndex === 0
            ? "clinicalDoctors"
            : "clinicalHygienists"
          : "nonClinical"
      const { updatedEntry, updatedAggregation } = updateProvider({
        original: action.payload?.original,
        field,
        value: sanityValue.value,
        aggregations: state.aggregations[dataIndex].data,
        providers: state.providerState.data,
        index: state.primaryIndex,
      })

      // Adjust index: table index 0 is "+ Add" button, data starts from index 1
      const dataIndex_adjusted = index - 1

      // Update the current tab's data
      if (state.primaryIndex === 0) {
        // Clinical mode - update the specific tab's data
        const tabKey =
          state.secondaryIndex === 0 ? "doctorsData" : "hygienistsData"
        if (state[tabKey as keyof typeof state]) {
          // Use the adjusted index for the data array
          ;(state[tabKey as keyof typeof state] as any[])[dataIndex_adjusted] =
            updatedEntry
        }
        state.data[dataIndex_adjusted] = updatedEntry
      } else {
        // Non-clinical mode - update both nonClinicalData and state.data
        if (state.nonClinicalData) {
          state.nonClinicalData[dataIndex_adjusted] = updatedEntry
        }
        state.data[dataIndex_adjusted] = updatedEntry
      }

      state.aggregations[dataIndex].data = updatedAggregation

      const cellId = `${dataIndex_adjusted}_${field}`
      // For transient new entry row (index 1), don't mark dirty or log changes
      if (state.editState.newEntry && index === 1) {
        return
      }
      const monthState = getMonthEditState(state, state.month)

      if (state.primaryIndex === 0) {
        if (!monthState[tabKeyForPersistence].dirty.includes(cellId)) {
          monthState[tabKeyForPersistence].dirty.push(cellId)
        }
      } else {
        if (!monthState.nonClinical.dirty.includes(cellId)) {
          monthState.nonClinical.dirty.push(cellId)
        }
      }
      const currentTabDirty =
        state.primaryIndex === 0
          ? monthState[tabKeyForPersistence].dirty
          : monthState.nonClinical.dirty
      monthState.dirty = [...currentTabDirty]
      syncMonthStateToMain(state, state.month)

      // Update modified data snapshot for this month/tab
      if (state.primaryIndex === 0) {
        const tabDataKey =
          state.secondaryIndex === 0 ? "doctorsData" : "hygienistsData"
        if ((state as any)[tabDataKey]) {
          monthState.modifiedData[tabDataKey] = [
            ...((state as any)[tabDataKey] as any[]),
          ]
        }
      } else if (state.nonClinicalData) {
        monthState.modifiedData.nonClinicalData = [...state.nonClinicalData]
      }

      upsertChangeForTab(
        state,
        tabKeyForPersistence,
        {
          ...updatedEntry,
          row_index: dataIndex_adjusted,
        },
        state.month,
      )
    },
    monthIncrement: (state, _) => {
      clearNewEntryRow(state)
      syncMainToMonthState(state, state.month)
      state.month += 1
      restoreMonthData(state, state.month)
      syncMonthStateToMain(state, state.month)
    },
    monthDecrement: (state: any, _) => {
      clearNewEntryRow(state)
      syncMainToMonthState(state, state.month)
      state.month -= 1
      restoreMonthData(state, state.month)
      syncMonthStateToMain(state, state.month)
    },
    setCopyToOptions: (state, action) => {
      state.copyTo.data = action.payload
    },
    setCopyTo: (state, action) => {
      state.copyTo.selected.push(action.payload)
    },
    unsetCopyTo: (state, action) => {
      state.copyTo.selected = state.copyTo.selected.filter((month: number) => {
        return action.payload !== month
      })
    },
    setCopyToAll: (state, action) => {
      if (action.payload) {
        state.copyTo.selectAll = action.payload
        state.copyTo.selected = Array.from({ length: 12 }, (_, i) => i + 1)
      } else {
        state.copyTo.selectAll = action.payload
        state.copyTo.selected = []
      }
    },
    setSavedData: (state, action) => {
      state.published = action.payload?.is_published

      // Determine if this is clinical or non-clinical data based on _secondaryIndex
      const dataSecondaryIndex = action.payload?._secondaryIndex

      // Process the budget data using the robust calculation system
      const rawBudgetData = action.payload?.budget || []
      console.log("🔵 [setSavedData] Raw API Budget Data:", {
        tab:
          dataSecondaryIndex === 0
            ? "Doctors"
            : dataSecondaryIndex === 1
            ? "Hygienists"
            : "Non-Clinical",
        count: rawBudgetData.length,
        items: rawBudgetData.map((item: any) => ({
          id: item.id,
          provider_sk: item.provider_sk,
          resource_name: item.resource_name,
        })),
      })
      let processedBudgetData = processBudgetData(rawBudgetData)
      console.log("🟢 [setSavedData] Processed Budget Data:", {
        count: processedBudgetData.length,
        items: processedBudgetData.map((item: any) => ({
          id: item.id,
          provider_sk: item.provider_sk,
          resource_name: item.resource_name,
        })),
      })

      // Filter out providers that are not in the selected group's provider list
      // This prevents cross-group provider leakage (e.g., Highland/Stanley providers showing in 805 Dentistry)
      // Use the correct provider list based on dataSecondaryIndex (doctors vs hygienists)
      let providerList: any[] = []
      if (dataSecondaryIndex !== undefined) {
        // Clinical mode - use the appropriate provider list
        if (dataSecondaryIndex === 0) {
          providerList = state.providerState?.doctors || []
        } else if (dataSecondaryIndex === 1) {
          providerList = state.providerState?.hygienists || []
        }
      } else if (state.primaryIndex === 1) {
        // Non-clinical mode - providers not applicable, skip filtering
        providerList = []
      }

      // Only filter if we have a valid provider list AND providers are not currently loading
      // This prevents race conditions where API data arrives before provider list is loaded
      // If providers are still loading, accept all API data and filter will happen on next update
      const providersLoading = state.providerState?.loading || false
      const hasValidProviderList = 
        providerList &&
        Array.isArray(providerList) &&
        providerList.length > 0 &&
        !providersLoading

      if (hasValidProviderList) {
        const validProviderSks = new Set(
          providerList
            .map((p: any) => p?.provider_sk)
            .filter((sk: any) => sk !== undefined && sk !== null),
        )
        // Only keep budget entries for providers that are in the selected group's provider list
        processedBudgetData = processedBudgetData.filter((item: any) => {
          // For clinical data, filter by provider_sk
          if (item?.provider_sk !== undefined && item?.provider_sk !== null) {
            return validProviderSks.has(item.provider_sk)
          }
          // If no provider_sk, include it (might be non-clinical or legacy data)
          return true
        })
        console.log("🟡 [setSavedData] Filtered data using provider list:", {
          providerListCount: providerList.length,
          validProviderSks: Array.from(validProviderSks),
          beforeFilter: rawBudgetData.length,
          afterFilter: processedBudgetData.length,
        })
      } else {
        // Providers not loaded yet or still loading - accept all API data
        // This prevents data loss when API response arrives before provider list
        console.log("🟡 [setSavedData] Skipping provider filter - providers not ready:", {
          providerListCount: providerList.length,
          providersLoading,
          acceptingAllData: processedBudgetData.length,
        })
      }

      const monthState = getMonthEditState(state, state.month)

      if (dataSecondaryIndex !== undefined) {
        // Clinical mode - determine which tab this data belongs to
        const secondaryTabKey =
          dataSecondaryIndex === 0
            ? secondayTabs.doctors
            : secondayTabs.hygienists

        // Store the processed budget data in the appropriate tab
        const tabDataKey =
          dataSecondaryIndex === 0 ? "doctorsData" : "hygienistsData"

        console.log("🟡 [setSavedData] Tab:", tabDataKey, "Month:", state.month)

        // Start with API data - preserve the order from API
        let finalData = [...processedBudgetData]
        console.log("🟡 [setSavedData] Initial finalData (from API):", {
          count: finalData.length,
          items: finalData.map((item: any) => ({
            id: item.id,
            provider_sk: item.provider_sk,
            resource_name: item.resource_name,
          })),
        })

        // Merge with modified data for this month/tab if present
        // Use provider_sk as the unique identifier (not id) to prevent duplicates
        const modifiedDataForTab = monthState.modifiedData[tabDataKey]
        console.log("🟡 [setSavedData] Modified Data for Tab:", {
          exists: !!modifiedDataForTab,
          count: modifiedDataForTab?.length || 0,
          items:
            modifiedDataForTab?.map((item: any) => ({
              id: item.id,
              provider_sk: item.provider_sk,
              resource_name: item.resource_name,
            })) || [],
        })
        if (modifiedDataForTab && modifiedDataForTab.length > 0) {
          // Create a set of provider_sk from current API data (authoritative source)
          const apiProviderSks = new Set(
            processedBudgetData.map((item: any) => item.provider_sk),
          )

          // Filter modifiedData to only include items with valid provider_sk
          // (items without provider_sk are invalid and should be ignored)
          const validModifiedData = modifiedDataForTab.filter((item: any) => {
            return item?.provider_sk !== undefined && item?.provider_sk !== null
          })

          // Deduplicate validModifiedData by provider_sk (keep last occurrence)
          const deduplicatedModifiedData = new Map()
          validModifiedData.forEach((item: any) => {
            deduplicatedModifiedData.set(item.provider_sk, item)
          })
          const uniqueModifiedData = Array.from(
            deduplicatedModifiedData.values(),
          )

          console.log("🟡 [setSavedData] Filtered Modified Data:", {
            originalCount: modifiedDataForTab.length,
            validCount: validModifiedData.length,
            uniqueCount: uniqueModifiedData.length,
            apiProviderSks: Array.from(apiProviderSks),
            modifiedProviderSks: uniqueModifiedData.map(
              (item: any) => item.provider_sk,
            ),
          })

          // Create a map using provider_sk as key (unique identifier)
          const modifiedMap = new Map(
            uniqueModifiedData.map((item: any) => [item.provider_sk, item]),
          )

          // For API items, use modified version if exists (to preserve local edits)
          // Preserve the original order from API
          finalData = processedBudgetData.map((apiItem: any) => {
            const modifiedItem = modifiedMap.get(apiItem.provider_sk)
            // If modified item exists and has local changes, merge them
            // But use API's id since that's the authoritative one
            if (modifiedItem) {
              return { ...modifiedItem, id: apiItem.id }
            }
            return apiItem
          })

          // Add items from modifiedData that are NOT in API data
          // (these would be locally added providers not yet synced)
          // Only add if they have a valid provider_sk and are not already in the data
          const newItemsToAdd: any[] = []
          uniqueModifiedData.forEach((modifiedItem: any) => {
            if (
              modifiedItem?.provider_sk &&
              !apiProviderSks.has(modifiedItem.provider_sk) &&
              // Ensure this item is not already in finalData (double-check)
              !finalData.some(
                (item: any) => item?.provider_sk === modifiedItem.provider_sk,
              )
            ) {
              newItemsToAdd.push(modifiedItem)
              finalData.push(modifiedItem)
            }
          })
          console.log("🟡 [setSavedData] After merge with modifiedData:", {
            count: finalData.length,
            newItemsAdded: newItemsToAdd.length,
            newItems: newItemsToAdd.map((item: any) => ({
              id: item.id,
              provider_sk: item.provider_sk,
              resource_name: item.resource_name,
            })),
            allItems: finalData.map((item: any) => ({
              id: item.id,
              provider_sk: item.provider_sk,
              resource_name: item.resource_name,
            })),
          })
        }

        // Filter out any "Total" aggregation rows that might have been accidentally added
        const dataWithoutTotal = finalData.filter((item: any) => {
          const isTotal =
            item?.resource_name === "Total" ||
            item?.resource_name === "Total Doctors" ||
            item?.resource_name === "Total Hygienists"
          if (isTotal) {
            console.warn(
              "⚠️ [setSavedData] Found Total row in finalData, removing:",
              item,
            )
          }
          return !isTotal
        })
        if (dataWithoutTotal.length !== finalData.length) {
          console.warn(
            "⚠️ [setSavedData] Removed",
            finalData.length - dataWithoutTotal.length,
            "Total row(s) from finalData",
          )
        }

        // Deduplicate finalData by provider_sk before storing to prevent duplicates
        // Preserve the order from API (items that came from API first, then new items)
        const seenProviderSks = new Set()
        const deduplicatedFinalData: any[] = []
        const duplicatesFound: any[] = []

        // First pass: add items in order, skipping duplicates
        dataWithoutTotal.forEach((item: any, index: number) => {
          if (item?.provider_sk !== undefined && item?.provider_sk !== null) {
            if (!seenProviderSks.has(item.provider_sk)) {
              seenProviderSks.add(item.provider_sk)
              deduplicatedFinalData.push(item)
            } else {
              // Found a duplicate - log details
              const existingItem = deduplicatedFinalData.find(
                (existing: any) => existing.provider_sk === item.provider_sk,
              )
              duplicatesFound.push({
                index,
                item: {
                  id: item.id,
                  provider_sk: item.provider_sk,
                  resource_name: item.resource_name,
                },
                existingItem: {
                  id: existingItem?.id,
                  provider_sk: existingItem?.provider_sk,
                  resource_name: existingItem?.resource_name,
                },
                reason: "duplicate provider_sk",
              })
            }
          } else {
            // For items without provider_sk (shouldn't happen for clinical, but handle gracefully)
            // Check if we've seen this item before by comparing all fields
            const isDuplicate = deduplicatedFinalData.some((existing: any) => {
              return (
                !existing?.provider_sk &&
                existing?.resource_name === item?.resource_name &&
                existing?.id === item?.id
              )
            })
            if (!isDuplicate) {
              deduplicatedFinalData.push(item)
            } else {
              duplicatesFound.push({
                index,
                item: {
                  id: item.id,
                  resource_name: item.resource_name,
                },
                reason: "no provider_sk but duplicate",
              })
            }
          }
        })

        console.log("🔴 [setSavedData] After Deduplication:", {
          beforeCount: finalData.length,
          afterCount: deduplicatedFinalData.length,
          duplicatesRemoved: duplicatesFound.length,
          duplicates: duplicatesFound,
          finalItems: deduplicatedFinalData.map((item: any) => ({
            id: item.id,
            provider_sk: item.provider_sk,
            resource_name: item.resource_name,
          })),
        })

        state[tabDataKey] = deduplicatedFinalData
        // Determine the tab key for persistence to check for dirty cells
        const tabKeyForPersistence: TabKeyType =
          dataSecondaryIndex === 0 ? "clinicalDoctors" : "clinicalHygienists"
        const monthStateForTab = monthState[tabKeyForPersistence]
        const hasLocalChanges =
          monthStateForTab &&
          monthStateForTab.dirty &&
          monthStateForTab.dirty.length > 0

        // Only save to modifiedData if there are actual local changes (dirty cells)
        // This prevents stale data from being persisted when API data is fresh
        if (hasLocalChanges) {
          monthState.modifiedData[tabDataKey] = [...deduplicatedFinalData]
        } else {
          // Clear modifiedData if there are no local changes to prevent stale data
          monthState.modifiedData[tabDataKey] = []
        }

        console.log("🟣 [setSavedData] Final State:", {
          tabDataKey,
          stateCount: state[tabDataKey]?.length,
          hasLocalChanges,
          dirtyCellsCount: monthStateForTab?.dirty?.length || 0,
          monthStateCount: monthState.modifiedData[tabDataKey]?.length,
        })

        // Set the current data only if we're viewing this tab
        if (
          state.primaryIndex === 0 &&
          state.secondaryIndex === dataSecondaryIndex
        ) {
          state.data = deduplicatedFinalData
        }

        // Calculate and store aggregations for this tab
        const aggregatedTotals = calculateAggregatedTotals(
          deduplicatedFinalData,
        )
        state.aggregations[secondaryTabKey].data = {
          resource_name: `Total`,
          ...aggregatedTotals,
        }
      } else {
        // Non-clinical mode
        // Use id as unique identifier for non-clinical (they don't have provider_sk)
        // Start with API data - preserve the order from API
        let finalData = [...processedBudgetData]
        console.log(
          "🟡 [setSavedData] Non-Clinical - Initial finalData (from API):",
          {
            count: finalData.length,
            items: finalData.map((item: any) => ({
              id: item.id,
              resource_name: item.resource_name,
            })),
          },
        )

        if (
          monthState.modifiedData.nonClinicalData &&
          monthState.modifiedData.nonClinicalData.length > 0
        ) {
          console.log("🟡 [setSavedData] Non-Clinical - Modified Data:", {
            exists: true,
            count: monthState.modifiedData.nonClinicalData.length,
            items: monthState.modifiedData.nonClinicalData.map((item: any) => ({
              id: item.id,
              resource_name: item.resource_name,
            })),
          })

          // Create a set of ids from current API data (authoritative source)
          const apiIds = new Set(
            processedBudgetData.map((item: any) => item.id),
          )

          // Filter modifiedData to only include items with valid id
          const validModifiedData =
            monthState.modifiedData.nonClinicalData.filter((item: any) => {
              return item?.id !== undefined && item?.id !== null
            })

          // Deduplicate validModifiedData by id (keep last occurrence)
          const deduplicatedModifiedData = new Map()
          validModifiedData.forEach((item: any) => {
            deduplicatedModifiedData.set(item.id, item)
          })
          const uniqueModifiedData = Array.from(
            deduplicatedModifiedData.values(),
          )

          console.log(
            "🟡 [setSavedData] Non-Clinical - Filtered Modified Data:",
            {
              originalCount: monthState.modifiedData.nonClinicalData.length,
              validCount: validModifiedData.length,
              uniqueCount: uniqueModifiedData.length,
              apiIds: Array.from(apiIds),
              modifiedIds: uniqueModifiedData.map((item: any) => item.id),
            },
          )

          // For non-clinical, use id as unique key
          const modifiedMap = new Map(
            uniqueModifiedData.map((item: any) => [item.id, item]),
          )

          // Preserve the original order from API
          finalData = processedBudgetData.map((apiItem: any) => {
            const modifiedItem = modifiedMap.get(apiItem.id)
            return modifiedItem || apiItem
          })

          // Add items from modifiedData that are NOT in API data
          // Only add if they have a valid id and are not already in the data
          const newItemsToAdd: any[] = []
          uniqueModifiedData.forEach((modifiedItem: any) => {
            if (
              modifiedItem?.id &&
              !apiIds.has(modifiedItem.id) &&
              // Ensure this item is not already in finalData (double-check)
              !finalData.some((item: any) => item?.id === modifiedItem.id)
            ) {
              newItemsToAdd.push(modifiedItem)
              finalData.push(modifiedItem)
            }
          })

          console.log(
            "🟡 [setSavedData] Non-Clinical - After merge with modifiedData:",
            {
              count: finalData.length,
              newItemsAdded: newItemsToAdd.length,
              newItems: newItemsToAdd.map((item: any) => ({
                id: item.id,
                resource_name: item.resource_name,
              })),
              allItems: finalData.map((item: any) => ({
                id: item.id,
                resource_name: item.resource_name,
              })),
            },
          )
        }

        // Filter out any "Total" aggregation rows that might have been accidentally added
        const nonClinicalDataWithoutTotal = finalData.filter((item: any) => {
          const isTotal =
            item?.resource_name === "Total" ||
            item?.resource_name === "Total Staff"
          if (isTotal) {
            console.warn(
              "⚠️ [setSavedData] Found Total row in non-clinical finalData, removing:",
              item,
            )
          }
          return !isTotal
        })

        // Deduplicate finalData by id before storing to prevent duplicates (non-clinical uses id)
        // Preserve the order from API
        const seenIds = new Set()
        const deduplicatedNonClinicalData: any[] = []
        const duplicatesFound: any[] = []

        nonClinicalDataWithoutTotal.forEach((item: any, index: number) => {
          if (item?.id !== undefined && item?.id !== null) {
            if (!seenIds.has(item.id)) {
              seenIds.add(item.id)
              deduplicatedNonClinicalData.push(item)
            } else {
              // Found a duplicate - log details
              const existingItem = deduplicatedNonClinicalData.find(
                (existing: any) => existing.id === item.id,
              )
              duplicatesFound.push({
                index,
                item: {
                  id: item.id,
                  resource_name: item.resource_name,
                },
                existingItem: {
                  id: existingItem?.id,
                  resource_name: existingItem?.resource_name,
                },
                reason: "duplicate id",
              })
            }
          } else {
            // For items without id, use resource_name + role_category as composite key
            const compositeKey = `${item?.resource_name || ""}_${
              item?.role_category || ""
            }`
            if (!seenIds.has(compositeKey)) {
              seenIds.add(compositeKey)
              deduplicatedNonClinicalData.push(item)
            } else {
              duplicatesFound.push({
                index,
                item: {
                  resource_name: item.resource_name,
                  role_category: item.role_category,
                },
                reason: "duplicate composite key",
              })
            }
          }
        })

        console.log("🔴 [setSavedData] Non-Clinical - After Deduplication:", {
          beforeCount: nonClinicalDataWithoutTotal.length,
          afterCount: deduplicatedNonClinicalData.length,
          duplicatesRemoved: duplicatesFound.length,
          duplicates: duplicatesFound,
          finalItems: deduplicatedNonClinicalData.map((item: any) => ({
            id: item.id,
            resource_name: item.resource_name,
          })),
        })

        state.nonClinicalData = deduplicatedNonClinicalData

        // Only save to modifiedData if there are actual local changes (dirty cells)
        // This prevents stale data from being persisted when API data is fresh
        const monthStateForTab = monthState.nonClinical
        const hasLocalChanges =
          monthStateForTab &&
          monthStateForTab.dirty &&
          monthStateForTab.dirty.length > 0

        if (hasLocalChanges) {
          monthState.modifiedData.nonClinicalData = [
            ...deduplicatedNonClinicalData,
          ]
        } else {
          // Clear modifiedData if there are no local changes to prevent stale data
          monthState.modifiedData.nonClinicalData = []
        }

        console.log("🟣 [setSavedData] Non-Clinical - Final State:", {
          stateCount: deduplicatedNonClinicalData.length,
          monthStateCount: monthState.modifiedData.nonClinicalData?.length || 0,
          hasLocalChanges,
          dirtyCellsCount: monthStateForTab?.dirty?.length || 0,
        })

        // Set the current data only if we're in non-clinical mode
        if (state.primaryIndex === 1) {
          state.data = deduplicatedNonClinicalData
        }

        // Calculate and store aggregations for non-clinical
        const aggregatedTotals = calculateAggregatedTotals(
          deduplicatedNonClinicalData,
        )
        state.aggregations[primaryTabs.nonClinical].data = {
          resource_name: `Total`,
          ...aggregatedTotals,
        }
      }
    },
    updateDirtyList: (state, action) => {
      const monthState = getMonthEditState(state, state.month)
      const tabKey: TabKeyType =
        state.primaryIndex === 0
          ? state.secondaryIndex === 0
            ? "clinicalDoctors"
            : "clinicalHygienists"
          : "nonClinical"
      monthState[tabKey].dirty = monthState[tabKey].dirty.filter(
        (id: string) => id !== action.payload,
      )
      monthState.dirty = [...monthState[tabKey].dirty]
      syncMonthStateToMain(state, state.month)
    },
    updateNewEntriesList: (state, action) => {
      const monthState = getMonthEditState(state, state.month)
      const newObject = action.payload.newObject
      const rowIndex = action.payload?.id - 1 // row.index is 1-based, data array is 0-based
      
      console.log("🟢 [updateNewEntriesList] Updating new entry with API response:", {
        rowIndex,
        newObjectId: newObject?.id,
        newObjectName: newObject?.resource_name || `${newObject?.firstname} ${newObject?.lastname}`,
      })

      if (state.primaryIndex === 0) {
        const tabKey =
          state.secondaryIndex === 0 ? "clinicalDoctors" : "clinicalHygienists"
        monthState[tabKey].newEntry = false
      } else {
        monthState.nonClinical.newEntry = false
      }
      monthState.newEntry = false
      syncMonthStateToMain(state, state.month)
      
      // Update state.data with the new object from API (includes the real ID)
      if (state.data[rowIndex]) {
        // Create new array reference to ensure Redux detects the change
        const updatedData = [...state.data]
        updatedData[rowIndex] = { ...newObject }
        state.data = updatedData
        console.log("🟢 [updateNewEntriesList] Updated state.data at index", rowIndex, "with ID:", newObject?.id)
      }

      // CRITICAL: Also update the tab-specific data arrays with the new ID
      if (state.primaryIndex === 0) {
        const tabDataKey =
          state.secondaryIndex === 0 ? "doctorsData" : "hygienistsData"
        if (state[tabDataKey as keyof typeof state]) {
          const tabData = state[tabDataKey as keyof typeof state] as any[]
          // Find the row by matching the first row (index 0) or by temporary ID
          const updatedTabData = tabData.map((item: any, index: number) => {
            // The newly created row is at index 0 (first row after the "+ Add" row)
            if (index === 0) {
              console.log("🟢 [updateNewEntriesList] Updating", tabDataKey, "at index 0:", {
                oldId: item?.id,
                newId: newObject?.id,
              })
              return { ...newObject }
            }
            return item
          })
          // Create new array reference
          ;(state as any)[tabDataKey] = [...updatedTabData]
          
          // Also update monthState.modifiedData
          if (monthState.modifiedData[tabDataKey]) {
            const updatedModifiedData = monthState.modifiedData[tabDataKey]!.map((item: any, index: number) => {
              if (index === 0) {
                return { ...newObject }
              }
              return item
            })
            monthState.modifiedData[tabDataKey] = [...updatedModifiedData]
          }
        }
      } else {
        if (state.nonClinicalData) {
          const updatedNonClinical = state.nonClinicalData.map((item: any, index: number) => {
            if (index === 0) {
              console.log("🟢 [updateNewEntriesList] Updating nonClinicalData at index 0:", {
                oldId: item?.id,
                newId: newObject?.id,
              })
              return { ...newObject }
            }
            return item
          })
          state.nonClinicalData = [...updatedNonClinical]
          
          if (monthState.modifiedData.nonClinicalData) {
            const updatedModifiedData = monthState.modifiedData.nonClinicalData.map((item: any, index: number) => {
              if (index === 0) {
                return { ...newObject }
              }
              return item
            })
            monthState.modifiedData.nonClinicalData = [...updatedModifiedData]
          }
        }
      }

      // Clear dirty cells for the newly created row (row index 1)
      // Remove any dirty cells that start with "0_" (since the new row is at index 0 in data array)
      if (state.primaryIndex === 0) {
        // Clinical mode - clear tab-specific dirty cells
        const tabKey =
          state.secondaryIndex === 0 ? "clinicalDoctors" : "clinicalHygienists"
        monthState[tabKey].dirty = monthState[tabKey].dirty.filter(
          (cellId: string) => !cellId.startsWith("0_"),
        )
      } else {
        // Non-clinical mode - clear non-clinical dirty cells
        monthState.nonClinical.dirty = monthState.nonClinical.dirty.filter(
          (cellId: string) => !cellId.startsWith("0_"),
        )
      }

      // Also clear from global dirty array
      const currentTabDirty =
        state.primaryIndex === 0
          ? state.secondaryIndex === 0
            ? monthState.clinicalDoctors.dirty
            : monthState.clinicalHygienists.dirty
          : monthState.nonClinical.dirty
      monthState.dirty = [...currentTabDirty]
      syncMonthStateToMain(state, state.month)

      // Reset the newly created row to normal state (allow editing of all fields)
      // The row is now at index 0 in the data array after successful creation
      if (state.data[0]) {
        // Remove any temporary ID and ensure it has proper structure
        const createdRow = state.data[0] as any
        if (
          createdRow.id &&
          typeof createdRow.id === "number" &&
          createdRow.id > 1000000000000
        ) {
          // This was a temporary ID, replace with the actual ID from API response
          state.data[0] = {
            ...createdRow,
            id: action.payload.newObject.id || createdRow.id,
          }
        }
      }
    },
    deleteProvider: (state, action) => {
      const strFields = ["resource_name", "role_category", "role_title"]
      const monthState = getMonthEditState(state, state.month)
      const deleteId = action.payload

      console.log("🗑️ [deleteProvider] Starting delete with ID:", deleteId, {
        primaryIndex: state.primaryIndex,
        secondaryIndex: state.secondaryIndex,
        currentDataCount: state.data.length,
        currentDataIds: state.data.map((d: any) => d?.id),
      })

      // Helper function to remove item and update aggregations
      const removeItemAndUpdateAggregations = (item: any) => {
        Object.keys(item).forEach((key) => {
          if (!strFields.includes(key)) {
            const prev = state.aggregations[state.dataIndex].data[key]
            const val = parseFloat(prev) - parseFloat(item[key])
            state.aggregations[state.dataIndex].data[key] = condenseNumber(
              val.toString(),
            )
          }
        })
      }

      // Determine which tab data to update
      const tabDataKey =
        state.primaryIndex === 0
          ? state.secondaryIndex === 0
            ? "doctorsData"
            : "hygienistsData"
          : "nonClinicalData"
      
      const tabKeyForPersistence: TabKeyType =
        state.primaryIndex === 0
          ? state.secondaryIndex === 0
            ? "clinicalDoctors"
            : "clinicalHygienists"
          : "nonClinical"

      if (state.editState.newEntry && deleteId === 1) {
        // Deleting a new entry (first row)
        // Reset newEntry state for the current tab
        if (state.primaryIndex === 0) {
          const tabKey =
            state.secondaryIndex === 0
              ? "clinicalDoctors"
              : "clinicalHygienists"
          state.editState[tabKey].newEntry = false
        } else {
          state.editState.nonClinical.newEntry = false
        }
        state.editState.newEntry = false

        // Update aggregations
        if (state.data[0]) {
          removeItemAndUpdateAggregations(state.data[0])
        }

        // Remove from the current tab's data
        if (state.primaryIndex === 0) {
          const tabKey = tabDataKey as "doctorsData" | "hygienistsData"
          if (state[tabKey as keyof typeof state]) {
            const currentData = state[tabKey as keyof typeof state] as any[]
            ;(state as any)[tabKey] = currentData.slice(1)
            // CRITICAL: Remove from monthState.modifiedData
            if (monthState.modifiedData[tabKey]) {
              monthState.modifiedData[tabKey] =
                monthState.modifiedData[tabKey]!.slice(1)
            }
          }
        } else {
          if (state.nonClinicalData) {
            state.nonClinicalData = state.nonClinicalData.slice(1)
            if (monthState.modifiedData.nonClinicalData) {
              monthState.modifiedData.nonClinicalData =
                monthState.modifiedData.nonClinicalData.slice(1)
            }
          }
        }
        state.data = state.data.slice(1)
      } else {
        // Deleting an existing entry - remove by ID from ALL locations
        
        // Find the item to delete (for aggregations update)
        let itemToDelete: any = null

        // Remove from state.data FIRST - create new array to ensure Redux detects change
        const beforeDataCount = state.data.length
        const filteredData = state.data.filter((item: any) => {
          if (item?.id === deleteId) {
            itemToDelete = item
            return false
          }
          return true
        })
        // Assign new array reference to ensure Redux detects the change
        state.data = [...filteredData]
        const afterDataCount = state.data.length
        console.log("🗑️ [deleteProvider] Removed from state.data:", {
          before: beforeDataCount,
          after: afterDataCount,
          removed: beforeDataCount - afterDataCount,
          deleteId,
        })

        // Remove from tab-specific data (doctorsData/hygienistsData/nonClinicalData)
        if (state.primaryIndex === 0) {
          const tabKey = tabDataKey as "doctorsData" | "hygienistsData"
          if (state[tabKey as keyof typeof state]) {
            const beforeTabCount = (state[tabKey as keyof typeof state] as any[]).length
            const filteredTabData = (
              state[tabKey as keyof typeof state] as any[]
            ).filter((item: any) => {
              if (item?.id === deleteId && !itemToDelete) {
                itemToDelete = item
              }
              return item?.id !== deleteId
            })
            const afterTabCount = filteredTabData.length
            console.log("🗑️ [deleteProvider] Removed from", tabKey, ":", {
              before: beforeTabCount,
              after: afterTabCount,
              removed: beforeTabCount - afterTabCount,
              deleteId,
            })
            // Assign new array reference to ensure Redux detects the change
            ;(state as any)[tabKey] = [...filteredTabData]
            // CRITICAL: Remove from monthState.modifiedData
            if (monthState.modifiedData[tabKey]) {
              const beforeModCount = monthState.modifiedData[tabKey]!.length
              monthState.modifiedData[tabKey] = monthState.modifiedData[
                tabKey
              ]!.filter((item: any) => item?.id !== deleteId)
              const afterModCount = monthState.modifiedData[tabKey]!.length
              console.log("🗑️ [deleteProvider] Removed from monthState.modifiedData", tabKey, ":", {
                before: beforeModCount,
                after: afterModCount,
                removed: beforeModCount - afterModCount,
              })
            }
          }
        } else {
          if (state.nonClinicalData) {
            const beforeCount = state.nonClinicalData.length
            const filteredNonClinical = state.nonClinicalData.filter(
              (item: any) => {
                if (item?.id === deleteId && !itemToDelete) {
                  itemToDelete = item
                }
                return item?.id !== deleteId
              },
            )
            // Assign new array reference to ensure Redux detects the change
            state.nonClinicalData = [...filteredNonClinical]
            const afterCount = state.nonClinicalData.length
            console.log("🗑️ [deleteProvider] Removed from nonClinicalData:", {
              before: beforeCount,
              after: afterCount,
              removed: beforeCount - afterCount,
              deleteId,
            })
            if (monthState.modifiedData.nonClinicalData) {
              const beforeModCount = monthState.modifiedData.nonClinicalData.length
              monthState.modifiedData.nonClinicalData =
                monthState.modifiedData.nonClinicalData.filter(
                  (item: any) => item?.id !== deleteId,
                )
              const afterModCount = monthState.modifiedData.nonClinicalData.length
              console.log("🗑️ [deleteProvider] Removed from monthState.modifiedData.nonClinicalData:", {
                before: beforeModCount,
                after: afterModCount,
                removed: beforeModCount - afterModCount,
              })
            }
          }
        }

        // CRITICAL: Remove from month-specific edit state data arrays
        if (monthState[tabKeyForPersistence]?.data) {
          const beforeEditCount = monthState[tabKeyForPersistence].data.length
          monthState[tabKeyForPersistence].data = monthState[
            tabKeyForPersistence
          ].data.filter((item: any) => item?.id !== deleteId)
          const afterEditCount = monthState[tabKeyForPersistence].data.length
          console.log("🗑️ [deleteProvider] Removed from monthState", tabKeyForPersistence, ".data:", {
            before: beforeEditCount,
            after: afterEditCount,
            removed: beforeEditCount - afterEditCount,
          })
        }

        // Update aggregations if we found the item
        if (itemToDelete) {
          removeItemAndUpdateAggregations(itemToDelete)
        } else {
          console.warn("🗑️ [deleteProvider] ⚠️ Item not found for aggregations update, deleteId:", deleteId)
        }
      }
      if (state.aggregations[state.dataIndex].data["production"]) {
        state.aggregations[state.dataIndex].data["compensation_percent"] =
          (state.aggregations[state.dataIndex].data["compensation_amount"] /
            state.aggregations[state.dataIndex].data["production"]) *
          100
      } else {
        state.aggregations[state.dataIndex].data["compensation_percent"] = 0.0
      }
      state.editState.dirty = state.editState.dirty.map((key: string) => {
        // Parse the cell ID to get row and field parts
        const parts = key.split("_")
        if (parts.length >= 2) {
          const rowIndex = parseInt(parts[0])
          const field = parts.slice(1).join("_")
          // Decrement the row index since we removed the first row
          return `${rowIndex - 1}_${field}`
        }
        return key
      })

      // Update month-specific dirty cells (using tabKeyForPersistence declared above)
      monthState[tabKeyForPersistence].dirty = monthState[
        tabKeyForPersistence
      ].dirty.map((key: string) => {
        const parts = key.split("_")
        if (parts.length >= 2) {
          const rowIndex = parseInt(parts[0])
          const field = parts.slice(1).join("_")
          return `${rowIndex - 1}_${field}`
        }
        return key
      })
      monthState.dirty = [...monthState[tabKeyForPersistence].dirty]
      syncMonthStateToMain(state, state.month)
    },
    // New actions for persistent state management
    saveChangeToTab: (state, action) => {
      const { tabKey, change } = action.payload
      if (!tabKey || !change) {
        return
      }
      upsertChangeForTab(state, tabKey, change)
    },
    clearTabChanges: (state, action) => {
      const { tabKey, month } = action.payload
      const targetMonth = month ?? state.month
      const monthState = getMonthEditState(state, targetMonth)
      const editStateKey = tabKey as keyof typeof monthState
      if (monthState[editStateKey]) {
        ;(monthState[editStateKey] as any).dirty = []
        ;(monthState[editStateKey] as any).newEntry = false
        ;(monthState[editStateKey] as any).data = []
      }
      monthState.dirty = []
      if (targetMonth === state.month) {
        syncMonthStateToMain(state, state.month)
      }
    },
    syncToPNL: (state, action) => {
      state.editState.allChanges = []
      // Clear all month-specific states
      Object.values(state.editState.monthStates || {}).forEach(
        (monthState: any) => {
          monthState.clinicalDoctors.data = []
          monthState.clinicalHygienists.data = []
          monthState.nonClinical.data = []
          monthState.clinicalDoctors.dirty = []
          monthState.clinicalHygienists.dirty = []
          monthState.nonClinical.dirty = []
          monthState.dirty = []
          monthState.allChanges = []
          monthState.modifiedData = {}
          monthState.newEntry = false
        },
      )

      // Also clear main state (current month)
      state.editState.clinicalDoctors.data = []
      state.editState.clinicalHygienists.data = []
      state.editState.nonClinical.data = []
      state.editState.clinicalDoctors.dirty = []
      state.editState.clinicalHygienists.dirty = []
      state.editState.nonClinical.dirty = []
      state.editState.dirty = []
      state.editState.newEntry = false
    },
    cancelNewEntry: (state) => {
      clearNewEntryRow(state)
    },
    clearAllData: (state) => {
      // Clear all data arrays
      state.data = []
      state.doctorsData = []
      state.hygienistsData = []
      state.nonClinicalData = []

      // Clear all month-specific states and modifiedData
      Object.keys(state.editState.monthStates || {}).forEach((monthKey) => {
        const monthState = state.editState.monthStates[parseInt(monthKey)]
        if (monthState) {
          monthState.clinicalDoctors.data = []
          monthState.clinicalHygienists.data = []
          monthState.nonClinical.data = []
          monthState.clinicalDoctors.dirty = []
          monthState.clinicalHygienists.dirty = []
          monthState.nonClinical.dirty = []
          monthState.dirty = []
          monthState.allChanges = []
          monthState.modifiedData = {
            doctorsData: [],
            hygienistsData: [],
            nonClinicalData: [],
          }
          monthState.newEntry = false
          monthState.clinicalDoctors.newEntry = false
          monthState.clinicalHygienists.newEntry = false
          monthState.nonClinical.newEntry = false
        }
      })

      // Clear main edit state
      state.editState.clinicalDoctors.data = []
      state.editState.clinicalHygienists.data = []
      state.editState.nonClinical.data = []
      state.editState.clinicalDoctors.dirty = []
      state.editState.clinicalHygienists.dirty = []
      state.editState.nonClinical.dirty = []
      state.editState.dirty = []
      state.editState.newEntry = false
      state.editState.clinicalDoctors.newEntry = false
      state.editState.clinicalHygienists.newEntry = false
      state.editState.nonClinical.newEntry = false
      state.editState.allChanges = []

      // Reset aggregations to initial state
      state.aggregations = {
        [primaryTabs.nonClinical]: {
          data: { resource_name: "Total" },
          key: "Staff",
        },
        [secondayTabs.doctors]: {
          data: { resource_name: "Total" },
          key: "Doctors",
        },
        [secondayTabs.hygienists]: {
          data: { resource_name: "Total" },
          key: "Hygienists",
        },
      }

      // Clear provider state
      state.providerState.doctors = []
      state.providerState.hygienists = []
      state.providerState.data = []
      state.providerState.loading = false

      // Reset month to 1
      state.month = 1
    },
  },
})

export default budgetSlice.reducer
export const {
  setPrimaryTab,
  setSecondayTab,
  togglePublished,
  setYears,
  setSelectedYear,
  loadProviders,
  loadingProviders,
  appendProvider,
  updateAppendedProvider,
  monthIncrement,
  monthDecrement,
  cancelNewEntry,
  setCopyToOptions,
  setCopyTo,
  unsetCopyTo,
  setCopyToAll,
  setSavedData,
  updateDirtyList,
  updateNewEntriesList,
  deleteProvider,
  saveChangeToTab,
  clearTabChanges,
  syncToPNL,
  clearAllData,
} = budgetSlice.actions
